import {
  alpha,
  Box,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  useTheme
} from '@mui/material'
import { EventCoverButton, EventCoverButtonRemove } from 'assets/icons'
import IconButton from 'components/buttons/IconButton'
import { useConnection } from 'components/providers/ConnectionProvider'
import GroupcalTooltip from 'components/tooltips/GroupcalTooltip'
import { IEventDetails } from 'hooks/useEventDetails'
import i18n from 'i18n'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'
import Logger from 'services/Logger'
import { EventNote } from 'types/api/changes'
import { useFilePicker } from 'use-file-picker'
import { compressBase64 } from 'utils/file'
import { capitalizeFirstChar } from 'utils/strings'

import { getCoverImageNote } from './EventCoverImage'
import ImageCropDialog from './ImageCropDialog'

interface GalleryButtonProps {
  eventHook: IEventDetails
}

export default function GalleryButton({ eventHook }: GalleryButtonProps) {
  const muiTheme = useTheme()
  const connection = useConnection()
  const menuAnchorRef = useRef<HTMLDivElement | null>(null)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { openFilePicker, filesContent, clear, errors } = useFilePicker({
    readAs: 'DataURL',
    accept: 'image/*',
    multiple: false
  })

  Logger.red('GalleryButton:Errors', errors)

  const coverImageNote = getCoverImageNote(eventHook.getValues('Notes'))
  const hasCoverImage = !!coverImageNote

  // State to handle the raw selected image for cropping
  const [selectedImageSrc, setSelectedImageSrc] = useState<string | null>(null)

  Logger.blue('GalleryButton', 'Rendering GalleryButton', eventHook.getValues())

  const handleMenuOpen = () => {
    if (menuAnchorRef.current) {
      setAnchorEl(menuAnchorRef.current)
    }
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleUploadClick = () => {
    handleMenuClose()
    openFilePicker()
  }

  const handleRemoveClick = () => {
    const currentNotes = eventHook.notes ?? []
    const filteredNotes = currentNotes.filter((n) => n.NoteType !== '1')
    eventHook.setNotes(filteredNotes)
    handleMenuClose()
  }

  useEffect(() => {
    Logger.debug('GalleryButton', 'filesContent changed', filesContent)
    if (filesContent.length > 0 && connection.isConnectionAvailable()) {
      // Pass the raw image directly to our cropper dialog instead of compressing right away
      setSelectedImageSrc(filesContent[0].content)
    }
  }, [filesContent])

  const handleCropSave = (croppedData: string) => {
    const currentNotes =
      typeof eventHook.getValues('Notes') === 'string'
        ? []
        : (eventHook.getValues('Notes') ?? [])

    // Remove existing cover image note if any
    const filteredNotes = currentNotes.filter((n) => n.NoteType !== '1')

    const coverNote: EventNote = {
      FilePath: 'null',
      Note: capitalizeFirstChar(t('photo')),
      NoteID: Date.now(),
      Status: 1,
      FileBytes: croppedData,
      NoteType: '1'
    }

    eventHook.setNotes([coverNote, ...filteredNotes])

    // Clear states
    setSelectedImageSrc(null)
    clear()
  }

  const handleCropCancel = () => {
    setSelectedImageSrc(null)
    clear()
  }

  return (
    <Box sx={{ alignItems: 'end' }}>
      <GroupcalTooltip title={t('eventCoverImage')}>
        <Box
          ref={menuAnchorRef}
          sx={{ display: 'inline-flex', alignItems: 'end' }}
        >
          <IconButton onClick={handleMenuOpen} size="md" stopPropagationOnClick>
            <EventCoverButton
              style={{
                color: hasCoverImage
                  ? 'white'
                  : muiTheme.palette.input.placeholder
              }}
            />
          </IconButton>
        </Box>
      </GroupcalTooltip>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          sx: {
            padding: '16px 0px 8px 0px'
          }
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 260,
              borderRadius: '12px',
              boxShadow:
                muiTheme.palette.mode === 'dark'
                  ? '0px 10px 28px rgba(0,0,0,0.5)'
                  : '0px 8px 24px rgba(0,0,0,0.14)',
              backgroundColor: muiTheme.palette.background.paper,
              color: muiTheme.palette.text.primary,
              border: `1px solid ${muiTheme.palette.border.primary}`
            }
          }
        }}
      >
        <Box
          display={'flex'}
          flexDirection={'row'}
          alignItems={'center'}
          mb={2}
        >
          <Typography
            ml={i18n.dir() === 'ltr' ? 4 : undefined}
            mr={i18n.dir() === 'rtl' ? 4 : undefined}
            width={'fit-content'}
            noWrap
            overflow={'visible'}
            variant="subtitle2"
            fontWeight={700}
            textTransform={'uppercase'}
            fontSize={'11px'}
            color={muiTheme.palette.text.secondary}
          >
            {t('eventCoverImage')}
          </Typography>
        </Box>

        {!hasCoverImage && (
          <MenuItem
            onClick={handleUploadClick}
            sx={{
              mx: 1,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: muiTheme.palette.surface.hover
              }
            }}
          >
            <ListItemIcon
              sx={{
                height: 16,
                width: 16,
                minWidth: '24px !important',
                color: muiTheme.palette.input.placeholder
              }}
            >
              <EventCoverButton
                style={{
                  color: alpha(muiTheme.palette.text.primary, 0.8),
                  width: 16,
                  height: 16
                }}
              />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                fontSize: '14px',
                color: muiTheme.palette.text.primary
              }}
            >
              {t('uploadFromYourComputer')}
            </ListItemText>
          </MenuItem>
        )}

        {hasCoverImage && (
          <MenuItem
            onClick={handleRemoveClick}
            sx={{
              mx: 1,
              borderRadius: '8px',
              '&:hover': {
                backgroundColor: muiTheme.palette.surface.hover
              }
            }}
          >
            <ListItemIcon
              sx={{
                height: 16,
                width: 16,
                minWidth: '24px !important',
                color: muiTheme.palette.input.placeholder
              }}
            >
              <EventCoverButtonRemove
                style={{
                  color: muiTheme.palette.input.placeholder,
                  width: 16,
                  height: 16
                }}
              />
            </ListItemIcon>
            <ListItemText
              primaryTypographyProps={{
                fontSize: '14px',
                color: muiTheme.palette.text.primary
              }}
            >
              {t('removeCoverImage')}
            </ListItemText>
          </MenuItem>
        )}
      </Menu>

      {selectedImageSrc && (
        <ImageCropDialog
          open={!!selectedImageSrc}
          imageSrc={selectedImageSrc}
          onClose={handleCropCancel}
          onCropSave={handleCropSave}
        />
      )}
    </Box>
  )
}
